/**
 * Makanly Brand Configuration
 * Design System v2.0.0 - Apple Aesthetic / Monochrome with Warm Neutrals
 *
 * Central brand config for all Makanly applications.
 * This is the PRIMARY source of truth for brand identity across the platform.
 *
 * @see /design/design.json for complete design system specification
 */

export const makanlyBrand = {
  name: 'Makanly',
  tagline: 'Where every order just flows.',
  shortMark: 'M',

  // Design System Version
  designSystemVersion: '2.0.0',
  theme: 'Apple Aesthetic / Monochrome with Warm Neutrals',

  // Assets
  assets: {
    logo: '/images/logo-512x512.png',
    logoHorizontal: '/assets/brand/logos/logo-horizontal-makanly-hres.png',
    logoHighRes: '/assets/brand/logos/logo-high-res.png',
    favicon: '/favicon.ico',
    appleTouchIcon: '/images/logo-512x512.png',
    // Mobile App Store
    appStore: '/assets/brand/logos/appstore.png',
    playStore: '/assets/brand/logos/playstore.png',
    // iOS App Icons
    iosIconSet: '/assets/brand/logos/Assets.xcassets/AppIcon.appiconset',
    // Android App Icons
    androidIconSet: '/assets/brand/logos/android',
  },

  // =====================================
  // BRAND COLORS (Design System v2.0.0)
  // =====================================

  // Primary - Teal (#587B7F)
  // F&B-appropriate, calming, professional brand identity
  primaryColor: '#587B7F',

  // Accent - Gold (#E2C044)
  // Success, rewards, loyalty, premium features
  accentColor: '#E2C044',

  // Neutral - Warm Gray (#1B1B1E)
  // Dark buttons, primary dark text
  neutralDark: '#1B1B1E',

  // Background - Light warm gray
  backgroundColor: '#F7F7F6',

  // Text - Primary dark
  textColor: '#1B1B1E',

  // Secondary text
  secondaryTextColor: '#63605C',

  // Card background
  cardBackgroundColor: '#ffffff',

  // Border color
  borderColor: '#E2E0DC',

  // Semantic Colors
  semantic: {
    success: '#10B981', // Emerald
    warning: '#F59E0B', // Amber
    danger: '#EF4444', // Red
    info: '#3B82F6', // Blue (use sparingly)
  },

  // QR Branding
  qrMarkText: 'Makanly',
  qrOverlayBg: '#ffffff',
  qrOverlayTextColor: '#1B1B1E',
  qrOverlayBorderColor: '#E2E0DC',

  // Typography
  typography: {
    fontFamily: {
      main: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      mono: "'SF Mono', 'Fira Code', 'Consolas', monospace",
    },
    // Primary font is Inter
    primaryFont: 'Inter',
  },

  // Domain Configuration
  baseDomain: 'makanly.com.my',

  // Subdomains
  subdomains: {
    landing: 'makanly.com.my',
    app: 'app.makanly.com.my',
    pos: 'pos.makanly.com.my',
    kds: 'kds.makanly.com.my',
    order: 'order.makanly.com.my',
    display: 'display.makanly.com.my',
    api: 'api.makanly.com.my',
    ws: 'api.makanly.com.my',
    docs: 'docs.makanly.com.my',
    status: 'status.makanly.com.my',
  },

  // Legal
  copyright: `© ${new Date().getFullYear()} Makanly. All rights reserved.`,
  poweredBy: 'Powered by Makanly',
};

export type MakanlyBrand = typeof makanlyBrand;
